/** Деталь прогона ИИ-агента: пер-товарные статусы, решения по спорным
 * матчам (связать / создать / пропустить), ретраи ошибок, ссылки в
 * карточки созданных черновиков.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Play, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

import { AiRun, RUN_STATUS_LABEL, RUN_STATUS_VARIANT } from "./ai_content";

interface AiItem {
  id: number;
  row_index: number;
  raw_name: string;
  code_1c: string | null;
  qty: number;
  cost_price: number | null;
  status:
    | "pending"
    | "processing"
    | "updated"
    | "created"
    | "needs_review"
    | "failed"
    | "skipped";
  product_id: number | null;
  product_name: string | null;
  flags: {
    needs_price?: boolean;
    auto_price_ready?: boolean;
    auto_price_enabled?: boolean;
    low_confidence?: boolean;
    llm_skipped?: boolean;
    code_1c_linked?: boolean;
    price_from_markup?: boolean;
    matched_by?: string | null;
    linked_stores?: string[];
  };
  match_candidates: { product_id: number; name: string; score: number }[];
  usage: { cost_usd?: number };
  error: string | null;
}

const ITEM_STATUS_LABEL: Record<AiItem["status"], string> = {
  pending: "В очереди",
  processing: "Обрабатывается",
  updated: "Обновлён",
  created: "Создан черновик",
  needs_review: "Нужно решение",
  failed: "Ошибка",
  skipped: "Пропущен",
};

const ITEM_STATUS_VARIANT: Record<
  AiItem["status"],
  "success" | "secondary" | "destructive" | "outline"
> = {
  pending: "outline",
  processing: "secondary",
  updated: "success",
  created: "success",
  needs_review: "destructive",
  failed: "destructive",
  skipped: "outline",
};

const FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "Все" },
  { value: "created", label: "Созданные" },
  { value: "updated", label: "Обновлённые" },
  { value: "needs_review", label: "Нужно решение" },
  { value: "failed", label: "Ошибки" },
  { value: "pending", label: "В очереди" },
  { value: "skipped", label: "Пропущенные" },
];

export function AiContentRunPage(): JSX.Element {
  const { runId } = useParams<{ runId: string }>();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");

  const { data: run } = useQuery({
    queryKey: ["ai-content-run", runId],
    queryFn: async () => (await api.get<AiRun>(`/ai-content/runs/${runId}`)).data,
    refetchInterval: (query) => {
      const data = query.state.data as AiRun | undefined;
      return data && (data.status === "pending" || data.status === "running")
        ? 3000
        : false;
    },
  });

  const isActive = run?.status === "pending" || run?.status === "running";

  const { data: items, isLoading } = useQuery({
    queryKey: ["ai-content-run-items", runId, filter],
    queryFn: async () => {
      const params = filter === "all" ? {} : { status: filter };
      return (
        await api.get<AiItem[]>(`/ai-content/runs/${runId}/items`, {
          params: { ...params, per_page: 200 },
        })
      ).data;
    },
    refetchInterval: isActive ? 4000 : false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["ai-content-run", runId] });
    queryClient.invalidateQueries({ queryKey: ["ai-content-run-items", runId] });
    queryClient.invalidateQueries({ queryKey: ["ai-content-runs"] });
  };

  const stats = run?.stats ?? {};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration/ai-content">
            <ArrowLeft className="w-4 h-4 mr-1" />
            ИИ-контент
          </Link>
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-heading">Прогон #{runId}</h1>
        {run && (
          <>
            <Badge variant={RUN_STATUS_VARIANT[run.status]}>
              {RUN_STATUS_LABEL[run.status]}
            </Badge>
            {isActive && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            <span className="text-sm text-muted-foreground">
              {run.file_name} · {run.model}
            </span>
          </>
        )}
      </div>

      {run && (
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>
            Обработано: <b>{stats.items_done ?? 0}</b>/{stats.items_total ?? 0}
          </span>
          <span>
            Создано: <b>{stats.items_created ?? 0}</b>
          </span>
          <span>
            Обновлено: <b>{stats.items_updated ?? 0}</b>
          </span>
          <span>
            Токены: {((stats.tokens_in ?? 0) / 1000).toFixed(0)}k вход /{" "}
            {((stats.tokens_out ?? 0) / 1000).toFixed(0)}k выход
          </span>
          {(stats.cost_usd ?? 0) > 0 && (
            <span>
              Стоимость: <b>≈${Number(stats.cost_usd).toFixed(2)}</b>
            </span>
          )}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Позиции</CardTitle>
          <div className="w-48">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40" />
          ) : !items || items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Нет позиций с таким статусом.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <ItemRow key={item.id} item={item} onChanged={invalidate} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ItemFlags({ item }: { item: AiItem }): JSX.Element {
  const f = item.flags ?? {};
  return (
    <div className="flex flex-wrap gap-1.5">
      {f.code_1c_linked && (
        <Badge variant="outline" className="text-[10px]">
          код 1С привязан к карточке
        </Badge>
      )}
      {f.llm_skipped && (
        <Badge variant="outline" className="text-[10px]">
          без ИИ (уже заполнен)
        </Badge>
      )}
      {f.auto_price_enabled && (
        <Badge variant="secondary" className="text-[10px]">
          автоцена включена
        </Badge>
      )}
      {f.price_from_markup && (
        <Badge variant="secondary" className="text-[10px]">
          цена от себестоимости (конкурент не найден)
        </Badge>
      )}
      {f.auto_price_ready && (
        <Badge variant="outline" className="text-[10px] text-amber-600">
          автоцена готова — введите себестоимость
        </Badge>
      )}
      {f.needs_price && (
        <Badge variant="outline" className="text-[10px] text-destructive">
          нужна цена
        </Badge>
      )}
      {f.low_confidence && (
        <Badge variant="outline" className="text-[10px] text-amber-600">
          низкая уверенность ИИ
        </Badge>
      )}
      {f.matched_by === "code_1c" && (
        <Badge variant="secondary" className="text-[10px]">
          найден по коду 1С
        </Badge>
      )}
      {(f.linked_stores ?? []).length > 0 && (
        <Badge variant="outline" className="text-[10px]">
          конкуренты: {(f.linked_stores ?? []).join(", ")}
        </Badge>
      )}
    </div>
  );
}

function ItemRow({
  item,
  onChanged,
}: {
  item: AiItem;
  onChanged: () => void;
}): JSX.Element {
  const retryMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/ai-content/items/${item.id}/retry`);
    },
    onSuccess: () => {
      toast.success("Позиция возвращена в очередь, прогон продолжен");
      onChanged();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось повторить"),
  });

  const resolveMutation = useMutation({
    mutationFn: async (payload: { action: string; product_id?: number }) => {
      await api.post(`/ai-content/items/${item.id}/resolve`, payload);
    },
    onSuccess: () => {
      toast.success("Решение принято");
      onChanged();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Не удалось применить решение"),
  });

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={ITEM_STATUS_VARIANT[item.status]} className="text-[10px]">
              {ITEM_STATUS_LABEL[item.status]}
            </Badge>
            <span className="text-sm font-medium break-all">{item.raw_name}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-2">
            <span>строка {item.row_index}</span>
            {item.code_1c && (
              <span className="font-mono">код 1С: {item.code_1c}</span>
            )}
            <span>остаток: {item.qty}</span>
            {item.cost_price !== null && (
              <span>себестоимость: {(item.cost_price / 100).toLocaleString("ru-RU")} сум</span>
            )}
            {(item.usage?.cost_usd ?? 0) > 0 && (
              <span>≈${Number(item.usage.cost_usd).toFixed(2)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {item.product_id && (
            <Button asChild variant="outline" size="sm" className="text-xs h-7">
              <Link to={`/products/${item.product_id}/edit`}>
                <ExternalLink className="w-3 h-3 mr-1" />
                {item.product_name
                  ? item.product_name.slice(0, 30)
                  : `товар #${item.product_id}`}
              </Link>
            </Button>
          )}
          {(item.status === "failed" || item.status === "skipped") && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3 mr-1" />
              )}
              Повторить
            </Button>
          )}
        </div>
      </div>

      <ItemFlags item={item} />

      {item.error && (
        <p className="text-xs text-destructive break-all">Ошибка: {item.error}</p>
      )}

      {item.status === "needs_review" && (
        <div className="bg-muted/40 rounded p-2.5 space-y-2">
          <p className="text-xs text-muted-foreground">
            Найдены похожие товары — выберите, к какому привязать (обновится
            остаток и дозаполнятся пустые поля), или создайте новую карточку:
          </p>
          {item.match_candidates.map((c) => (
            <div key={c.product_id} className="flex items-center justify-between gap-2 text-xs">
              <span className="break-all">
                {c.name}{" "}
                <span className="text-muted-foreground">({Math.round(c.score * 100)}%)</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-6 shrink-0"
                disabled={resolveMutation.isPending}
                onClick={() =>
                  resolveMutation.mutate({ action: "link", product_id: c.product_id })
                }
              >
                Связать
              </Button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              disabled={resolveMutation.isPending}
              onClick={() => resolveMutation.mutate({ action: "create" })}
            >
              <Play className="w-3 h-3 mr-1" />
              Создать новую карточку
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              disabled={resolveMutation.isPending}
              onClick={() => resolveMutation.mutate({ action: "skip" })}
            >
              Пропустить
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
